/**
 * Static subcategory mapping for each template type.
 * Keys must match the type's slug from the backend (lowercased, underscored).
 * To add new subcategories: just append to the relevant array — no DB changes needed.
 */
export const TEMPLATE_CATEGORIES = {
  flyer: [
    'Gym & Fitness', 'Medical', 'Real Estate', 'Night Club', 'Event', 'Music', 'DJ Party',
    'Restaurant', 'Grand Opening', 'Sale', 'Corporate', 'Business', 'Education',
    'Travel', 'Church', 'Salon', 'Fashion', 'Sports', 'Workshop', 'Tech',
  ],
  poster: [
    'Movie', 'Music', 'Event', 'Festival', 'Motivational', 'Awareness', 'Political',
    'School', 'College', 'Exhibition', 'Product', 'Brand', 'Fitness', 'Travel', 'Food',
    'Gaming', 'Tech', 'Startup', 'Social', 'Fashion',
  ],
  brochure: [
    'Restaurant', 'Hotel', 'Travel', 'Real Estate', 'Corporate', 'Medical', 'Education',
    'Salon', 'Gym', 'Interior', 'Architecture', 'Event', 'Photography', 'Marketing',
    'NGO', 'Finance', 'Insurance', 'Catalog', 'Startup', 'Consulting',
  ],
  logo: [
    'Minimal', 'Modern', 'Luxury', 'Real Estate', 'Fitness', 'Food', 'Fashion', 'Tech',
    'Startup', 'Gaming', 'Ecommerce', 'Education', 'Finance', 'Medical', 'Travel',
    'Agency', 'Photography', 'Personal', 'Sports', 'Construction',
  ],
  youtube_thumbnail: [
    'Vlog', 'Gaming', 'Reaction', 'Tech', 'Tutorial', 'Podcast', 'Fitness', 'Food',
    'Travel', 'Motivation', 'Finance', 'Crypto', 'News', 'Comedy', 'Education',
    'Interview', 'Review', 'Before After', 'Challenge', 'Story',
  ],
  youtube_banner: [
    'Gaming', 'Vlog', 'Tech', 'Podcast', 'Finance', 'Education', 'Motivation', 'Food',
    'Travel', 'Fitness', 'News', 'Kids', 'Music', 'Personal', 'Business', 'Startup',
    'Crypto', 'Real Estate', 'Coaching', 'Influencer',
  ],
  business_card: [
    'Corporate', 'Minimal', 'Luxury', 'Creative', 'Real Estate', 'Photographer',
    'Freelancer', 'Marketing', 'Consultant', 'Medical', 'Lawyer', 'Startup', 'Designer',
    'Developer', 'Trainer', 'Salon', 'Restaurant', 'Construction', 'Finance', 'Education',
  ],
  menu: [
    'Restaurant', 'Cafe', 'Fast Food', 'Fine Dining', 'Bar', 'Bakery', 'Dessert',
    'Pizza', 'Food Truck', 'Healthy', 'Vegan', 'Indian', 'Italian', 'Chinese', 'Mexican',
    'BBQ', 'Breakfast', 'Drinks', 'Seafood', 'Hotel',
  ],
  certificate: [
    'Achievement', 'Completion', 'Course', 'Employee', 'Excellence', 'Participation',
    'Appreciation', 'Training', 'Internship', 'School', 'College', 'Sports',
    'Online Course', 'Workshop', 'Seminar', 'Volunteer', 'Recognition', 'Honor',
    'Membership', 'Leadership',
  ],
  instagram_post: [
    'Business', 'Sale', 'Motivation', 'Quotes', 'Food', 'Travel', 'Fashion', 'Fitness',
    'Real Estate', 'Product', 'Event', 'Festival', 'Memes', 'Brand', 'Tips', 'Education',
    'Tech', 'Startup', 'Personal', 'Story',
  ],
  meme: [
    'Funny', 'Relatable', 'Office', 'Student', 'Relationship', 'Trending', 'Dark',
    'Desi', 'Gaming', 'Tech', 'Fitness', 'Food', 'Reaction', 'Pet', 'Movie',
    'Bollywood', 'Hollywood', 'Political', 'Daily', 'Internet',
  ],
  infographic: [
    'Business', 'Marketing', 'Education', 'Health', 'Finance', 'Process', 'Timeline',
    'Comparison', 'Startup', 'Social Media', 'SEO', 'AI', 'Environment', 'Survey',
    'Product', 'Case Study', 'Checklist', 'Guide', 'Steps', 'Charts',
  ],
  biodata: [
    'Modern', 'Professional', 'Creative', 'Minimal', 'Corporate', 'Fresher',
    'Experienced', 'Designer', 'Developer', 'Marketing', 'Finance', 'Medical',
    'Teacher', 'Student', 'Internship', 'Executive', 'ATS', 'Simple', 'Bold', 'Clean',
  ],
  pamphlet: [
    'Business', 'Event', 'Medical', 'Real Estate', 'Education', 'Product', 'Service',
    'Restaurant', 'Travel', 'Fitness', 'Salon', 'Marketing', 'Political', 'NGO',
    'Startup', 'Promotion', 'Festival', 'Discount', 'Awareness', 'Local',
  ],
  gift_certificate: [
    'Birthday', 'Wedding', 'Anniversary', 'Restaurant', 'Spa', 'Salon', 'Shopping',
    'Travel', 'Hotel', 'Fitness', 'Gaming', 'Online Store', 'Fashion', 'Food',
    'Holiday', 'Offer', 'Luxury', 'Corporate', 'Event', 'Festival',
  ],
  invitation: [
    'Wedding', 'Birthday', 'Party', 'Baby Shower', 'Engagement', 'Anniversary',
    'Corporate', 'Conference', 'Festival', 'Graduation', 'Housewarming', 'Dinner',
    'Holiday', 'Launch', 'Music', 'VIP', 'Kids', 'Formal', 'Casual', 'Celebration',
  ],
};

/**
 * Returns the subcategory list for the given type slug.
 * Normalises the slug (lowercase, spaces/hyphens → underscores) before lookup.
 * Returns [] when the type has no mapping — category field stays hidden/optional.
 *
 * @param {string} slug  e.g. "flyer", "instagram_post", "YouTube Thumbnail"
 * @returns {string[]}
 */
export const getCategoriesForSlug = (slug) => {
  if (!slug) return [];
  const key = slug.toLowerCase().replace(/[\s-]+/g, '_');
  return TEMPLATE_CATEGORIES[key] || [];
};
